import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Table, Container, Row, Col, Badge, Pagination, Spinner, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { BiPencil, BiUser, BiUserPlus, BiUserMinus, BiCheck } from 'react-icons/bi';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import api from '../../api';
import EditarCandidatoModal from './modal/EditarCandidatoModal';
import CandidatoResponsavelModal from './modal/CandidatoResponsavelModal';
import CandidatoViewModel from '../../viewmodel/CandidatoViewModel';

interface CandidatoEdital {
    id: string;
    inscricao: string;
    candidatoId: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoResponse {
    candidatoId: string;
    id?: string;
    nome: string;
    nomeSocial: string;
    candidatoEditais: CandidatoEdital[];
    cpf: string;
    passaporte: string;
    email: string;
    status: string;
    autodeclaracaoRacial?: string | null;
    candidatoResponsavel?: {
        nome: string;
        nomeSocial: string;
        cpf: string;
        passaporte: string;
        email: string;
    };
}

interface Estatisticas {
    total: number;
    validados: number;
    naoValidados: number;
    ausentes: number;
    inconclusivos: number;
    indefinidos: number;
}

interface CandidatosComEstatisticasResponse {
    candidatos: {
        content: CandidatoResponse[];
        totalElements: number;
        totalPages: number;
        first: boolean;
        last: boolean;
        numberOfElements: number;
    };
    estatisticas: Estatisticas;
}

interface CandidatosEdicaoListProps {
    processoId: string;
}

const CandidatosEdicaoList: React.FC<CandidatosEdicaoListProps> = ({ processoId }) => {
    const [candidatos, setCandidatos] = useState<CandidatoResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [currentCandidato, setCurrentCandidato] = useState<CandidatoResponse | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [exibirDeletados, setExibirDeletados] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editais, setEditais] = useState<Array<{ id: string; nome: string }>>([]);
    
    // Filtros
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroInscricao, setFiltroInscricao] = useState('');
    const [filtroCpf, setFiltroCpf] = useState('');
    const [filtroEmail, setFiltroEmail] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');
    const [showResponsavelModal, setShowResponsavelModal] = useState(false);
    const [currentResponsavel, setCurrentResponsavel] = useState<any>(null);
    

    // Função para renderizar múltiplas inscrições
    const renderInscricoes = (candidatoEditais: CandidatoEdital[]) => {
        if (candidatoEditais.length === 0) {
            return '-';
        } else if (candidatoEditais.length === 1) {
            return (
                <div>
                    {candidatoEditais[0].inscricao} - {candidatoEditais[0].nomeEdital}
                </div>
            );
        } else {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`tooltip-${candidatoEditais[0].candidatoId}`}>
                            {candidatoEditais.map((edital, index) => (
                                <div key={`${edital.id}-${index}`}>
                                    {edital.inscricao} - {edital.nomeEdital}
                                    {index < candidatoEditais.length - 1 && <br />}
                                </div>
                            ))}
                        </Tooltip>
                    }
                >
                    <Badge bg="secondary" className="cursor-pointer">
                        {candidatoEditais.length} inscrições
                    </Badge>
                </OverlayTrigger>
            );
        }
    };

    const fetchCandidatos = async () => {
        try {
            setLoading(true);
            const params: any = {
                page,
                size,
                exibirDeletados
            };
            if (filtroNome) params.nome = filtroNome;
            if (filtroInscricao) params.inscricao = filtroInscricao;
            if (filtroCpf) params.cpf = filtroCpf;
            if (filtroEmail) params.email = filtroEmail;
            if (filtroStatus) params.status = filtroStatus;

            const response = await api.get<CandidatosComEstatisticasResponse>(`/processos/${processoId}/candidatos/estatisticas`, { params });

            setCandidatos(response.data.candidatos.content);
            setTotalPages(response.data.candidatos.totalPages);
            setTotalElements(response.data.candidatos.totalElements);
            setEstatisticas(response.data.estatisticas);
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Erro ao carregar candidatos';
            toast.error(errorMsg);
            setCandidatos([]);
            setEstatisticas(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (processoId) {
            fetchProcesso();
            fetchCandidatos();
        }
    }, [processoId, page, size, filtroNome, filtroInscricao, filtroCpf, filtroEmail, filtroStatus, exibirDeletados]);

    const fetchProcesso = async () => {
        try {
            const response = await api.get(`/processos/${processoId}`);
            if (response.data.editais) {
                setEditais(response.data.editais.map((e: any) => ({ id: e.id, nome: e.nome })));
            }
        } catch (err: any) {
            console.error('Erro ao carregar processo:', err);
        }
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSize(Number(e.target.value));
        setPage(0);
    };

    const handleFiltroChange = (tipo: string, valor: string) => {
        setPage(0);
        switch (tipo) {
            case 'nome': setFiltroNome(valor); break;
            case 'inscricao': setFiltroInscricao(valor); break;
            case 'cpf': setFiltroCpf(valor); break;
            case 'email': setFiltroEmail(valor); break;
            case 'status': setFiltroStatus(valor); break;
        }
    };

    const limparFiltros = () => {
        setFiltroNome('');
        setFiltroInscricao('');
        setFiltroCpf('');
        setFiltroEmail('');
        setFiltroStatus('');
        setPage(0);
    };

    const handleEdit = (candidato: CandidatoResponse) => {
        setCurrentCandidato(candidato);
        setEditMode(true);
        setShowModal(true);
    };

    const handleView = (candidato: CandidatoResponse) => {
        setCurrentCandidato(candidato);
        setEditMode(false);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentCandidato(null);
    };

    const handleChange = (e: any) => {
        if (!currentCandidato) return;
        const { name, value } = e.target;

        if (name.startsWith('responsavel.')) {
            const key = name.split('.')[1];
            setCurrentCandidato({
                ...currentCandidato,
                candidatoResponsavel: {
                    ...(currentCandidato.candidatoResponsavel || {
                        nome: '', email: '', nomeSocial: '', cpf: '', passaporte: ''
                    }),
                    [key]: value
                }
            });
        } else {
            setCurrentCandidato({ ...currentCandidato, [name]: value });
        }
    };

    const handleAddResponsavel = () => {
        if (!currentCandidato) return;
        setCurrentCandidato({
            ...currentCandidato,
            candidatoResponsavel: {
                nome: '',
                email: '',
                nomeSocial: '',
                cpf: '',
                passaporte: ''
            }
        });
    };

    const handleRemoveResponsavel = () => {
        if (!currentCandidato) return;
        setCurrentCandidato({
            ...currentCandidato,
            candidatoResponsavel: undefined
        });
    };

    const handleSave = async () => {
        console.log('handleSave chamado');
        console.log('currentCandidato:', currentCandidato);
        console.log('processoId:', processoId);
        console.log('candidatoId:', currentCandidato?.candidatoId);
        
        if (!currentCandidato) {
            console.error('currentCandidato é null');
            toast.error('Nenhum candidato selecionado');
            return;
        }
        
        if (!processoId) {
            console.error('processoId é null');
            toast.error('ID do processo não encontrado');
            return;
        }
        
        const candidatoId = currentCandidato.candidatoId || currentCandidato.id;
        if (!candidatoId) {
            console.error('candidatoId é null ou undefined');
            toast.error('ID do candidato não encontrado');
            return;
        }

        console.log('Iniciando salvamento do candidato:', candidatoId);

        const requestBody = {
            nome: currentCandidato.nome,
            nomeSocial: currentCandidato.nomeSocial || '',
            cpf: currentCandidato.cpf || '',
            passaporte: currentCandidato.passaporte || '',
            email: currentCandidato.email,
            autodeclaracaoRacial: currentCandidato.autodeclaracaoRacial || null,
            status: currentCandidato.status,
            candidatoEditais: (currentCandidato.candidatoEditais || []).map(ce => ({
                inscricao: ce.inscricao,
                candidatoId: currentCandidato.candidatoId || currentCandidato.id,
                editalId: ce.editalId,
                nomeEdital: ce.nomeEdital
            })),
            responsavel: currentCandidato.candidatoResponsavel ? {
                nome: currentCandidato.candidatoResponsavel.nome,
                nomeSocial: currentCandidato.candidatoResponsavel.nomeSocial || '',
                cpf: currentCandidato.candidatoResponsavel.cpf || '',
                passaporte: currentCandidato.candidatoResponsavel.passaporte || '',
                email: currentCandidato.candidatoResponsavel.email
            } : null
        };

        console.log('Request body:', requestBody);

        try {
            setSaving(true);
            await CandidatoViewModel.updateCandidato(processoId, candidatoId, requestBody);
            toast.success('Candidato atualizado com sucesso!');
            handleCloseModal();
            fetchCandidatos();
        } catch (error: any) {
            console.error('Erro ao salvar candidato:', error);
            const errorMsg = error.response?.data?.message || 'Dados do Candidato ou Responsável inválidos ou não preenchidos.';
            toast.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const badgeMap: Record<string, string> = {
            'VALIDADO': 'success',
            'VALIDADO_ANTERIORMENTE': 'primary',
            'NAO_VALIDADO': 'danger',
            'NAO_AVALIADO': 'secondary',
            'INCONCLUSIVO': 'warning',
            'DELETADO': 'dark',
            'AUSENTE': 'info'
        };
        return <Badge bg={badgeMap[status] || 'light'}>{status}</Badge>;
    };

    return (
        <Container fluid className="py-4">
            {/* Header com Estatísticas */}
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col>
                            <h4 className="mb-0">Edição de Candidatos</h4>
                            <small className="text-muted">Visualize e edite os candidatos do processo</small>
                        </Col>
                        <Col xs="auto">
                            <div className="d-flex gap-2">
                                <Badge bg="info" className="fs-6">
                                    {totalElements} total
                                </Badge>
                                <Button
                                    variant={exibirDeletados ? "outline-secondary" : "secondary"}
                                    size="sm"
                                    onClick={() => setExibirDeletados(!exibirDeletados)}
                                >
                                    {exibirDeletados ? <BsEyeSlash /> : <BsEye />}
                                    {exibirDeletados ? ' Ocultar' : ' Mostrar'} Deletados
                                </Button>
                            </div>
                        </Col>
                    </Row>
                    {estatisticas && (
                        <Row className="mt-3">
                            <Col>
                                <div className="d-flex gap-3 flex-wrap">
                                    <small className="text-success">
                                        <strong>{estatisticas.validados}</strong> validados
                                    </small>
                                    <small className="text-danger">
                                        <strong>{estatisticas.naoValidados}</strong> não validados
                                    </small>
                                    <small className="text-secondary">
                                        <strong>{estatisticas.ausentes}</strong> ausentes
                                    </small>
                                    <small className="text-warning">
                                        <strong>{estatisticas.inconclusivos}</strong> inconclusivos
                                    </small>
                                    <small className="text-info">
                                        <strong>{estatisticas.indefinidos}</strong> indefinidos
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Filtros */}
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Nome</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Filtrar por nome..."
                                    value={filtroNome}
                                    onChange={(e) => handleFiltroChange('nome', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Inscrição</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Inscrição..."
                                    value={filtroInscricao}
                                    onChange={(e) => handleFiltroChange('inscricao', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>CPF</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="CPF..."
                                    value={filtroCpf}
                                    onChange={(e) => handleFiltroChange('cpf', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Email..."
                                    value={filtroEmail}
                                    onChange={(e) => handleFiltroChange('email', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={filtroStatus}
                                    onChange={(e) => handleFiltroChange('status', e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="VALIDADO">Validado</option>
                                    <option value="VALIDADO_ANTERIORMENTE">Validado anteriormente</option>
                                    <option value="NAO_VALIDADO">Não Validado</option>
                                    <option value="AUSENTE">Ausente</option>
                                    <option value="INCONCLUSIVO">Inconclusivo</option>
                                    <option value="NAO_AVALIADO">Não avaliado</option>
                                    <option value="DELETADO">Deletado</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={1} className="d-flex align-items-end">
                            <Button variant="outline-secondary" onClick={limparFiltros} className="w-100">
                                Limpar
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {loading ? (
                <Card className="shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" role="status" variant="primary">
                            <span className="visually-hidden">Carregando...</span>
                        </Spinner>
                        <p className="mt-3 text-muted">Carregando candidatos...</p>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Nome Social</th>
                                <th>Inscrições</th>
                                <th>CPF</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Responsável</th>
                                <th className="text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {candidatos.length > 0 ? (
                                candidatos.map((candidato) => (
                                    <tr key={candidato.id || candidato.candidatoId}>
                                        <td>{candidato.nome}</td>
                                        <td>{candidato.nomeSocial || '-'}</td>
                                        <td>{renderInscricoes(candidato.candidatoEditais)}</td>
                                        <td>{candidato.cpf}</td>
                                        <td>{candidato.email}</td>
                                        <td>{getStatusBadge(candidato.status)}</td>
                                        <td>
                                            {candidato.candidatoResponsavel ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <span>{candidato.candidatoResponsavel.nome}</span>
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </td>
                                        <td>
                                            <div className="text-center gap-1">                                                
                                                <Button variant="warning" size="sm" onClick={() => handleEdit(candidato)}>
                                                    <BiPencil />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="text-center">
                                        Nenhum candidato encontrado
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center gap-3">
                            <Form.Group className="mb-0">
                                <Form.Label className="me-2 mb-0">Itens por página:</Form.Label>
                                <Form.Select value={size} onChange={handleSizeChange} style={{ width: 'auto', display: 'inline-block' }}>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </Form.Select>
                            </Form.Group>
                            <small className="text-muted">
                                Mostrando {candidatos.length} de {totalElements} candidatos
                            </small>
                        </div>

                        {totalPages > 1 && (
                            <Pagination className="mb-0">
                                <Pagination.First onClick={() => handlePageChange(0)} disabled={page === 0} />
                                <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 0} />

                                {[...Array(totalPages).keys()].map((pageNumber) => (
                                    <Pagination.Item
                                        key={pageNumber}
                                        active={pageNumber === page}
                                        onClick={() => handlePageChange(pageNumber)}
                                    >
                                        {pageNumber + 1}
                                    </Pagination.Item>
                                ))}

                                <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1} />
                                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={page === totalPages - 1} />
                            </Pagination>
                        )}
                    </div>
                </>
            )}

            {/* Modal de Edição/Visualização */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editMode ? <BiPencil className="me-2" /> : <BsEye className="me-2" />}
                        {editMode ? 'Editar' : 'Visualizar'} Candidato
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentCandidato && (
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nome *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="nome"
                                            value={currentCandidato.nome}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nome Social</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="nomeSocial"
                                            value={currentCandidato.nomeSocial || ''}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Inscrição *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={currentCandidato.candidatoEditais?.[0]?.inscricao || ''}
                                            onChange={(e) => {
                                                const inscricao = e.target.value;
                                                if (currentCandidato.candidatoEditais && currentCandidato.candidatoEditais.length > 0) {
                                                    const updated = [...currentCandidato.candidatoEditais];
                                                    updated[0] = { ...updated[0], inscricao };
                                                    setCurrentCandidato({ ...currentCandidato, candidatoEditais: updated });
                                                }
                                            }}
                                            disabled={!editMode}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Edital *</Form.Label>
                                        <Form.Select
                                            value={currentCandidato.candidatoEditais?.[0]?.editalId || ''}
                                            onChange={(e) => {
                                                const editalId = e.target.value;
                                                const edital = editais.find(ed => ed.id === editalId);
                                                if (edital && currentCandidato.candidatoEditais && currentCandidato.candidatoEditais.length > 0) {
                                                    const updated = [...currentCandidato.candidatoEditais];
                                                    updated[0] = { ...updated[0], editalId: edital.id, nomeEdital: edital.nome };
                                                    setCurrentCandidato({ ...currentCandidato, candidatoEditais: updated });
                                                }
                                            }}
                                            disabled={!editMode}
                                            required
                                        >
                                            <option value="">Selecione</option>
                                            {editais.map(edital => (
                                                <option key={edital.id} value={edital.id}>{edital.nome}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>CPF</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cpf"
                                            value={currentCandidato.cpf}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Passaporte</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="passaporte"
                                            value={currentCandidato.passaporte || ''}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email *</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={currentCandidato.email}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            name="status"
                                            value={currentCandidato.status}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                        >
                                            <option value="VALIDADO">Validado</option>
                                            <option value="VALIDADO_ANTERIORMENTE">Validado anteriormente</option>
                                            <option value="NAO_VALIDADO">Não Validado</option>
                                            <option value="AUSENTE">Ausente</option>
                                            <option value="INCONCLUSIVO">Inconclusivo</option>
                                            <option value="NAO_AVALIADO">Não avaliado</option>
                                            <option value="DELETADO">Deletado</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Seção Responsável */}
                            <hr />
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6>Responsável</h6>
                                {editMode && (
                                    <div>
                                        {!currentCandidato.candidatoResponsavel ? (
                                            <Button variant="success" size="sm" onClick={handleAddResponsavel}>
                                                <BiUserPlus /> Adicionar
                                            </Button>
                                        ) : (
                                            <Button variant="danger" size="sm" onClick={handleRemoveResponsavel}>
                                                <BiUserMinus /> Remover
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {currentCandidato.candidatoResponsavel && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Nome do Responsável</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="responsavel.nome"
                                                    value={currentCandidato.candidatoResponsavel.nome}
                                                    onChange={handleChange}
                                                    disabled={!editMode}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Nome Social do Responsável</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="responsavel.nomeSocial"
                                                    value={currentCandidato.candidatoResponsavel.nomeSocial || ''}
                                                    onChange={handleChange}
                                                    disabled={!editMode}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>CPF do Responsável</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="responsavel.cpf"
                                                    value={currentCandidato.candidatoResponsavel.cpf || ''}
                                                    onChange={handleChange}
                                                    disabled={!editMode}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Passaporte do Responsável</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="responsavel.passaporte"
                                                    value={currentCandidato.candidatoResponsavel.passaporte || ''}
                                                    onChange={handleChange}
                                                    disabled={!editMode}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Email do Responsável</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="responsavel.email"
                                                    value={currentCandidato.candidatoResponsavel.email}
                                                    onChange={handleChange}
                                                    disabled={!editMode}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Fechar
                    </Button>
                    {editMode && (
                        <Button variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <BiCheck className="me-1" />
                                    Salvar
                                </>
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default CandidatosEdicaoList;
